import React from 'react'
import {
  Dropdown,
  DropdownTrigger,
  DropdownMenu,
  DropdownItem,
} from '@/design-system/molecules/Dropdown'
import { Icon } from '@/design-system/atoms/Icon'
import type {
  ChatSessionSource,
  ShareAccessLevel,
  UserMinimal,
} from '../../types/agents'

export interface SessionData {
  id: string
  title: string
  timestamp: string
  agentType: string
  messageCount: number
  status?: string
  source?: ChatSessionSource
  metadata?: Record<string, string>
  // Sharing fields
  isShared?: boolean
  isOwner?: boolean
  myAccessLevel?: ShareAccessLevel | 'owner' | null
  sharedBy?: UserMinimal | null
  participantsCount?: number
  // Unread tracking
  hasUnread?: boolean
}

// Sessões curtas não precisam exibir a contagem de mensagens visualmente;
// o badge só aparece quando há muitas mensagens.
const MIN_MESSAGE_COUNT_FOR_BADGE = 9

interface SessionListItemProps {
  session: SessionData
  isActive: boolean
  onSelect: (sessionId: string) => void
  onShare?: (session: SessionData) => void
  formatTime: (dateString: string) => string
}

export const SessionListItem: React.FC<SessionListItemProps> = ({
  session,
  isActive,
  onSelect,
  onShare,
  formatTime,
}) => {
  const getMetaValue = (keys: string[]): string | null => {
    if (!session.metadata) return null
    for (const key of keys) {
      const val = session.metadata[key]
      if (val && val.trim() !== '') return val.trim()
    }
    return null
  }

  const formatServiceOrder = (value: string): string | null => {
    const trimmed = value.trim()
    if (!trimmed) return null
    const normalized = trimmed.replace(/^OS[-\s]?/i, '')
    return `OS-${normalized}`
  }

  const serviceOrder = getMetaValue(['service_order', 'service_order_id', 'os'])
  const customer = getMetaValue(['customer', 'customer_name'])
  const plate = getMetaValue(['plate'])
  const vehicleModel = getMetaValue(['vehicle_model', 'model', 'vehicle'])

  const metadataItems = [
    serviceOrder ? formatServiceOrder(serviceOrder) : null,
    customer,
    plate,
    vehicleModel,
  ].filter(Boolean) as string[]

  // Detecta títulos genéricos (ex.: "OS #CSPS") que não distinguem sessões.
  // Quando o backend gera título pobre, promovemos o primeiro metadado útil
  // (chassi/cliente/placa) como subtítulo principal para o usuário identificar.
  const isGenericTitle =
    /^OS\s*#?CSPS$/i.test(session.title.trim()) || session.title.trim() === ''
  const primaryLabel =
    isGenericTitle && metadataItems[0] ? metadataItems[0] : session.title
  const secondaryLabel =
    isGenericTitle && metadataItems[0] ? session.title : null

  // Metadados visíveis: pula o primeiro item quando ele já foi promovido a
  // título principal (caso de título genérico).
  const visibleMetadata = secondaryLabel
    ? metadataItems.slice(1)
    : metadataItems

  const badgeStyles =
    'rounded-md bg-gray-100 dark:bg-gray-800/60 text-content-secondary'

  // Sharing status
  const isSharedSession =
    session.isShared || (session.participantsCount ?? 1) > 1
  const isOwner = session.isOwner !== false // Default to true if undefined
  const canShare = isOwner && onShare

  // Unread indicator for shared sessions
  const hasUnread = session.hasUnread && !isOwner

  // Access level indicator
  const getAccessBadge = () => {
    if (!isSharedSession) return null

    if (!isOwner && session.sharedBy) {
      return (
        <span
          className="inline-flex items-center gap-1 text-[10px] text-blue-600 dark:text-blue-400"
          title={`Compartilhado por ${session.sharedBy.fullName || session.sharedBy.username}`}
        >
          <Icon name="users" size={12} />
        </span>
      )
    }

    if (isOwner && isSharedSession) {
      return (
        <span
          className="inline-flex items-center gap-1 text-[10px] text-green-600 dark:text-green-400"
          title={`${session.participantsCount ?? 1} participantes`}
        >
          <Icon name="users" size={12} />
          <span>{session.participantsCount}</span>
        </span>
      )
    }

    return null
  }

  const handleShare = () => {
    onShare?.(session)
  }

  return (
    <div
      role="button"
      tabIndex={0}
      onClick={() => onSelect(session.id)}
      onKeyDown={e => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault()
          onSelect(session.id)
        }
      }}
      className={`relative flex items-center w-full gap-2 pl-3 pr-2 py-2.5 text-sm transition-colors duration-150 group focus:outline-none rounded-lg border-l-2 cursor-pointer ${
        isActive
          ? 'bg-brand-50 dark:bg-brand-950/30 border-l-brand-500'
          : 'bg-transparent border-l-transparent hover:bg-gray-50 dark:hover:bg-gray-800/40'
      }`}
    >
      {/* Content */}
      <div className="flex-1 min-w-0">
        {/* Row 1: Title + badge + time */}
        <div className="flex items-center gap-1.5">
          <h3
            className={`flex-1 min-w-0 text-xs font-semibold truncate text-left ${
              isActive
                ? 'text-brand-700 dark:text-brand-400'
                : 'text-content-primary'
            }`}
          >
            {primaryLabel}
          </h3>
          {session.source === 'scheduled' && (
            <span
              className="inline-flex items-center rounded-full bg-purple-100 dark:bg-purple-900/40 text-purple-700 dark:text-purple-300 text-[9px] font-semibold px-1.5 py-0.5"
              title="Sessão criada por agendamento automático"
            >
              Agendado
            </span>
          )}
          {getAccessBadge()}
          {hasUnread && (
            <span
              className="h-2 w-2 flex-shrink-0 rounded-full bg-brand-500 animate-pulse"
              title="Novas mensagens"
            />
          )}
          {(session.messageCount ?? 0) > MIN_MESSAGE_COUNT_FOR_BADGE && (
            <span className="flex-shrink-0 inline-flex items-center justify-center rounded-full text-[9px] leading-none font-semibold px-1.5 py-0.5 min-w-[18px] bg-gray-200 dark:bg-gray-700 text-content-secondary">
              {session.messageCount}
            </span>
          )}
          <span className="flex-shrink-0 text-[10px] tabular-nums text-content-tertiary">
            {formatTime(session.timestamp)}
          </span>

          {canShare && (
            <div
              className="flex-shrink-0 opacity-0 group-hover:opacity-100 transition-opacity"
              onClick={e => e.stopPropagation()}
            >
              <Dropdown>
                <DropdownTrigger>
                  <button
                    type="button"
                    className="p-0.5 rounded hover:bg-gray-200/50 dark:hover:bg-gray-700/50"
                    aria-label="Ações da sessão"
                  >
                    <Icon
                      name="ellipsis-vertical"
                      size={14}
                      className="text-gray-500 dark:text-gray-400"
                    />
                  </button>
                </DropdownTrigger>
                <DropdownMenu className="w-40">
                  <DropdownItem onClick={handleShare}>
                    <Icon name="users" size={14} />
                    Compartilhar
                  </DropdownItem>
                </DropdownMenu>
              </Dropdown>
            </div>
          )}
        </div>

        {/* Row 2: Secondary label (título original quando promovemos metadata) */}
        {secondaryLabel && (
          <div className="mt-0.5 text-[10px] text-content-tertiary truncate">
            {secondaryLabel}
          </div>
        )}

        {/* Row 3: Metadata badges (pula o primeiro item se já virou primary) */}
        {visibleMetadata.length > 0 && (
          <div className="mt-1 flex flex-wrap gap-1 text-[11px]">
            {visibleMetadata.map(item => (
              <span
                key={item}
                className={`inline-flex items-center px-1.5 py-0.5 ${badgeStyles}`}
              >
                <span className="max-w-[160px] truncate">{item}</span>
              </span>
            ))}
          </div>
        )}
      </div>
    </div>
  )
}
